export const PRODUCT_PART_DEFAULT = {
  part_management_fid: "",
  part_name: "",
  part_type: "",
  radius: "",
  length: "",
  width: "",
  height: "",
  unit: "",
  volume: "",
  rate: "",
};

export const UNIT_OPTIONS = [
  { label: "Millimeter (mm)", value: "mm" },
  { label: "Centimeter (cm)", value: "cm" },
  { label: "Meter (m)", value: "m" },
  { label: "Inch (in)", value: "in" },
  { label: "Feet (ft)", value: "ft" },
];

const UNIT_TO_MM = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4,
  ft: 304.8,
};

const DIMENSION_KEYS = ["radius", "length", "width", "height"];

export const PART_SPECIFICATION_DEFAULT = {
  part_type: "",
  radius: "",
  length: "",
  width: "",
  height: "",
  unit: "",
  volume: "",
  rate: "",
};

export const QUOTATION_ITEM_DEFAULT = {
  product_management_fid: "",
  product_name: "",
  volume: "",
  quantity: "",
  rate: "",
  amount: "",
};

export const QUOTATION_COST_DEFAULTS = {
  manufacturing_cost: "0",
  packaging_cost: "0",
  fob_cost: "0",
  finishing_cost: "0",
  tax: "0",
  total_amount: "0",
};

export const PO_ITEM_DEFAULT = {
  quotation_item_fid: "",
  quotation_fid: "",
  quotation_code: "",
  product_management_fid: "",
  product_name: "",
  volume: "",
  quoted_quantity: "",
  consumed_quantity: "",
  remaining_quantity: "",
  quantity: "",
  rate: "",
  amount: "",
};

const isEnterpriseField = (field = {}) => {
  const name = String(field.name || "").toLowerCase();
  const label = String(field.label || "").toLowerCase();
  return (
    name.includes("enterprise") ||
    name.includes("enterprize") ||
    label.includes("enterprise") ||
    label.includes("enterprize")
  );
};

export const orderFormFields = (fields = []) => {
  const enterpriseSelect = [];
  const otherSelects = [];
  const nonSelects = [];

  fields.forEach((field) => {
    if (field.type !== "select") {
      nonSelects.push(field);
      return;
    }

    if (isEnterpriseField(field)) {
      enterpriseSelect.push(field);
      return;
    }

    otherSelects.push(field);
  });

  return [...enterpriseSelect, ...otherSelects, ...nonSelects];
};

export const groupFieldsBySection = (fields = [], fallbackSectionTitle = "") => {
  const sectionOrder = [];
  const sectionMap = {};

  fields.forEach((field) => {
    const explicitSectionName =
      typeof field.section === "string" ? field.section.trim() : "";
    const sectionName = explicitSectionName || fallbackSectionTitle;
    const sectionKey = sectionName || "__default__";

    if (!sectionMap[sectionKey]) {
      sectionMap[sectionKey] = {
        title: sectionName || null,
        fields: [],
      };
      sectionOrder.push(sectionKey);
    }

    sectionMap[sectionKey].fields.push(field);
  });

  return sectionOrder.map((key) => sectionMap[key]);
};

export const getDimensionFieldMeta = (partType) => {
  const normalizedType = String(partType || "").toLowerCase();
  if (normalizedType === "circle") {
    return [{ name: "radius", label: "Radius" }];
  }
  if (normalizedType === "square") {
    return [
      { name: "length", label: "Side" },
      { name: "width", label: "Side" },
    ];
  }
  return [
    { name: "length", label: "Length" },
    { name: "width", label: "Width" },
    { name: "height", label: "Height" },
  ];
};

export const getActiveDimensionFields = (partType) =>
  getDimensionFieldMeta(partType).map((field) => field.name);

export const normalizeUnit = (unit) => {
  const normalized = String(unit || "").trim().toLowerCase();
  return UNIT_TO_MM[normalized] ? normalized : "";
};

const parseNumeric = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumeric = (value) => {
  if (!Number.isFinite(value)) return "";
  const rounded = Math.round((value + Number.EPSILON) * 1000000) / 1000000;
  return String(rounded);
};

export const convertDimensionValue = (value, fromUnit, toUnit) => {
  const numericValue = parseNumeric(value);
  if (numericValue === null) return value;

  const fromFactor = UNIT_TO_MM[normalizeUnit(fromUnit)];
  const toFactor = UNIT_TO_MM[normalizeUnit(toUnit)];
  if (!fromFactor || !toFactor) return value;

  const converted = (numericValue * fromFactor) / toFactor;
  return formatNumeric(converted);
};

export const clearInactiveDimensions = (row = {}, partType) => {
  const activeFields = getActiveDimensionFields(partType);
  return DIMENSION_KEYS.reduce((acc, key) => {
    acc[key] = activeFields.includes(key) ? row[key] : "";
    return acc;
  }, { ...row });
};

export const calculateVolumeFromRow = (row = {}) => {
  const normalizedType = String(row.part_type || "").toLowerCase();
  const radius = parseNumeric(row.radius);
  const length = parseNumeric(row.length);
  const width = parseNumeric(row.width);
  const height = parseNumeric(row.height);

  let computedVolume = null;
  if (normalizedType === "circle") {
    if (radius === null) return "";
    computedVolume = Math.PI * radius * radius;
  } else if (normalizedType === "square") {
    if (length === null || width === null) return "";
    computedVolume = length * width;
  } else if (normalizedType === "rectangle") {
    if (length === null || width === null || height === null) return "";
    computedVolume = length * width * height;
  } else {
    return "";
  }

  return formatNumeric(computedVolume);
};
